import { Box, type SxProps, type Theme } from "@mui/material";
import { type FC, type ReactNode, useEffect, useRef } from "react";

type Props = {
	/** The rendered bubbles/cards for the conversation. */
	children: ReactNode;
	/** Value that changes whenever the content grows (item count, last message length, status). A new value smooth-scrolls to the bottom. */
	scrollKey: unknown;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * The message-column scaffold for a chat surface: a leading flex column plus a
 * bottom anchor that smooth-scrolls into view whenever `scrollKey` changes. The
 * caller renders the individual bubbles/cards as children and decides what an
 * item is; this owns only the column layout + autoscroll.
 */
export const ChatMessageList: FC<Props> = ({ children, scrollKey, dataTestId, sx }) => {
	const bottomAnchorRef = useRef<HTMLDivElement | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: scrollKey is the intentional autoscroll trigger, not read in the body
	useEffect(() => {
		bottomAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
	}, [scrollKey]);

	return (
		// role="log" + polite live region so screen readers announce new messages and the "thinking"
		// status as they arrive, without stealing focus (WCAG 2.1 4.1.3 Status Messages).
		<Box
			data-testid={dataTestId}
			role="log"
			aria-live="polite"
			aria-relevant="additions text"
			aria-atomic={false}
			sx={[{ display: "flex", flexDirection: "column", gap: 1.25, width: "100%" }, ...(Array.isArray(sx) ? sx : [sx])]}
		>
			{children}
			<div ref={bottomAnchorRef} />
		</Box>
	);
};
